import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	Snackbar,
	Typography,
	CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";

import type { AssetPart } from "@/pages/asset-details/models/AssetPart";
import { PartsTable } from "./parts-table";
import {
	addLinkAssetPart,
	removePartLink,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";

interface Props {
	searchAssetParts: Function;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	AssetId: number | null;
	isLoading: boolean;
	listAllParts: AssetPart[];
}

export const PartsPicker: React.FC<Props> = ({
	searchAssetParts,
	show,
	onHide,
	AssetId,
	isLoading,
	listAllParts,
}) => {
	const {
		state: { listAssetParts },
	} = useAssetDetailsContext();

	const [selectedParts, setSelectedParts] = useState<AssetPart[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [loadingSave, setLoadingSave] = useState(false);

	useEffect(() => {
		if (show) {
			setSelectedParts([...listAssetParts]);
		}
	}, [show, listAssetParts]);

	const handleSelectionChanged = (newSelectedParts: AssetPart[]) => {
		setSelectedParts(newSelectedParts);
	};

	const handleSave = async () => {
		setLoadingSave(true);
		const partsToAdd = selectedParts.filter(
			(part) =>
				!listAssetParts.some((assetPart) => assetPart.Name === part.Name),
		);

		const partsToRemove = listAssetParts.filter(
			(part) =>
				!selectedParts.some((selectedPart) => selectedPart.Name === part.Name),
		);

		if (partsToAdd.length > 0) {
			try {
				for (const part of partsToAdd) {
					const res = await addLinkAssetPart(
						AssetId as number,
						part.Name ?? "",
						null,
					);
					if (!res.ok) {
						setError(res.message);
						setLoadingSave(false);
						return;
					}
				}
				setSuccessMessage("Parts added successfully");
			} catch (err) {
				setError("Failed to add parts");
			}
		}

		if (partsToRemove.length > 0) {
			try {
				for (const part of partsToRemove) {
					await removePartLink(AssetId as number, part.Name ?? "");
				}
				setSuccessMessage("Parts removed successfully");
			} catch (err) {
				setError("Failed to remove parts");
			}
		}

		setLoadingSave(false);
		if (partsToAdd.length > 0 || partsToRemove.length > 0) {
			onHide(true);
		} else {
			onHide(false);
		}
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						PARTS
					</Typography>
				</DialogTitle>
				<DialogContent>
					<PartsTable
						allParts={Object.values(listAllParts)}
						assetParts={listAssetParts}
						onSelectionChanged={handleSelectionChanged}
						isLoading={isLoading}
					/>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px 24px" }}>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item>
							<Button
								variant="contained"
								color="default"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>
						</Grid>
						<Grid item>
							<Button
								startIcon={loadingSave && <CircularProgress size="1rem" />}
								disabled={loadingSave}
								variant="contained"
								color="primary"
								onClick={handleSave}
							>
								Save
							</Button>
						</Grid>
					</Grid>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
